/**
 * BulkScreeningQuiz.jsx
 * Renders screening questions for MULTIPLE candidates in a single card.
 * Each candidate gets their own section with the same set of questions.
 * On submit, sends all answers to the backend for bulk shortlisting.
 */

import React, { useState } from "react";
import "../ChatPage.css";

export default function BulkScreeningQuiz({ data, onSubmit, disabled }) {
  const questions = data?.questions || [];
  const candidates = data?.candidates || [];
  const questionCount = questions.length;

  // answers[candidateIndex][questionIndex] = "user answer"
  const [answers, setAnswers] = useState(() =>
    candidates.map(() => questions.map(() => ""))
  );
  const [statuses, setStatuses] = useState(() =>
    candidates.map(() => "")
  );
  const [comments, setComments] = useState(() =>
    candidates.map(() => "")
  );
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (candIdx, qIdx, value) => {
    setAnswers((prev) => {
      const updated = prev.map((row) => [...row]);
      updated[candIdx][qIdx] = value;
      return updated;
    });
  };

  const handleStatusChange = (candIdx, value) => {
    setStatuses((prev) => {
      const updated = [...prev];
      updated[candIdx] = value;
      return updated;
    });
  };

  const handleCommentChange = (candIdx, value) => {
    setComments((prev) => {
      const updated = [...prev];
      updated[candIdx] = value;
      return updated;
    });
  };

  const allAnswered = candidates.every((_, ci) => {
    const allQAnswered = answers[ci].every((a) => a.trim().length > 0);
    const hasStatus = statuses[ci] !== "";
    const hasComment = statuses[ci] === "REJECT" || comments[ci].trim().length > 0;
    return allQAnswered && hasStatus && hasComment;
  });

  const handleSubmit = () => {
    if (!allAnswered || submitted || disabled) return;
    setSubmitted(true);

    const candidates_answers = candidates.map((name, ci) => ({
      candidateName: name,
      status: statuses[ci] === "REJECT" ? "REJECTED" : "ACCEPTED",
      comment: comments[ci],
      answers: questions.map((q, qi) => ({
        question: q.screenQuestion || q.question || "",
        given_answer: answers[ci][qi],
      })),
    }));

    const payload = {
      type: "bulk_screening",
      pending_action: data.pending_action,
      candidates_answers,
    };

    console.log("[BulkScreeningQuiz] Submitting payload:", JSON.stringify(payload).slice(0, 300));
    console.log("[BulkScreeningQuiz] payload.type:", payload.type);
    onSubmit(payload);
  };

  if (questions.length === 0 || candidates.length === 0) {
    return (
      <div className="hn-screening-card">
        <p className="hn-screening-empty">No screening questions or candidates found.</p>
      </div>
    );
  }

  return (
    <div className="hn-screening-card" style={{ maxWidth: '100%' }}>
      {/* Header */}
      <div className="hn-screening-header">
        <span className="hn-screening-badge">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 11l3 3L22 4" />
            <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
          </svg>
          Bulk Screening
        </span>
        <span className="hn-screening-count">
          {candidates.length} candidate{candidates.length !== 1 ? "s" : ""} · {questionCount} question{questionCount !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Per-candidate sections */}
      <div className="hn-screening-questions">
        {candidates.map((candidateName, ci) => (
          <div key={ci} style={{
            marginBottom: ci < candidates.length - 1 ? '20px' : '0',
            paddingBottom: ci < candidates.length - 1 ? '20px' : '0',
            borderBottom: ci < candidates.length - 1 ? '1px solid rgba(255,255,255,0.08)' : 'none',
          }}>
            {/* Candidate name header */}
            <div style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#7c8aff',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              {candidateName}
            </div>

            {/* Questions for this candidate */}
            {questions.map((q, qi) => {
              const questionText = q.screenQuestion || q.question || `Question ${qi + 1}`;
              return (
                <div key={qi} className="hn-screening-question">
                  <label className="hn-screening-label">
                    <span className="hn-screening-num">{qi + 1}.</span>
                    {questionText}
                  </label>
                  <input
                    type="text"
                    className="hn-screening-input"
                    placeholder="Type your answer..."
                    value={answers[ci][qi]}
                    onChange={(e) => handleChange(ci, qi, e.target.value)}
                    disabled={submitted || disabled}
                  />
                </div>
              );
            })}

            {/* Status and Comment */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '8px' }}>
              <div className="hn-screening-question" style={{ marginBottom: 0 }}>
                <label className="hn-screening-label">
                  Status <span style={{ color: '#ff4d4f' }}>*</span>
                </label>
                <select
                  className="hn-screening-input"
                  value={statuses[ci]}
                  onChange={(e) => handleStatusChange(ci, e.target.value)}
                  disabled={submitted || disabled}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid rgba(255, 255, 255, 0.1)', background: 'rgba(0, 0, 0, 0.2)', color: '#fff' }}
                >
                  <option value="">Select Status</option>
                  <option value="SHORTLIST">Move to shortlisting</option>
                  <option value="INTERVIEW">Move to interview</option>
                  <option value="REJECT">Reject</option>
                </select>
              </div>
              <div className="hn-screening-question" style={{ marginBottom: 0 }}>
                <label className="hn-screening-label">
                  Comment {statuses[ci] !== "REJECT" && <span style={{ color: '#ff4d4f' }}>*</span>}
                </label>
                <textarea
                  className="hn-screening-input"
                  placeholder="Add a comment..."
                  value={comments[ci]}
                  onChange={(e) => handleCommentChange(ci, e.target.value)}
                  disabled={submitted || disabled}
                  rows="2"
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid rgba(255, 255, 255, 0.1)', background: 'rgba(0, 0, 0, 0.2)', color: '#fff', resize: 'vertical' }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Submit button */}
      <button
        className={`hn-screening-submit ${
          !allAnswered || submitted || disabled ? "hn-screening-submit-disabled" : ""
        }`}
        onClick={handleSubmit}
        disabled={!allAnswered || submitted || disabled}
      >
        {submitted ? (
          <>
            <span className="hn-step-spinner" />
            Processing all candidates...
          </>
        ) : (
          <>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 2L11 13" />
              <path d="M22 2L15 22L11 13L2 9L22 2Z" />
            </svg>
            Submit All ({candidates.length} candidates)
          </>
        )}
      </button>
    </div>
  );
}
